import { useEffect, useState } from "react";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import { SoulTripWordmark } from "./GandabherundaMark";
import { ETV_WIN_SERIES_URL } from "../lib/logos";
import { Play } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/episodes", label: "Episodes" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  // Logo stays hidden until the splash flies into the nav (or 4.4s safety)
  const [logoReady, setLogoReady] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onSplashDone = () => setLogoReady(true);
    window.addEventListener("soultrip:splash-done", onSplashDone);
    // Safety: reveal nav logo even if splash event misses
    const safety = setTimeout(() => setLogoReady(true), 4400);
    return () => {
      window.removeEventListener("soultrip:splash-done", onSplashDone);
      clearTimeout(safety);
    };
  }, []);

  // Click on logo → scroll to top (smooth) if already on /, else navigate home then scroll
  const onLogoClick = (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
      // ScrollToTop in App.js handles the rest
    }
  };

  return (
    <nav
      data-testid="site-nav"
      className="fixed inset-x-0 top-0 z-[600] flex items-center justify-between px-6 md:px-12 py-3 md:py-4 transition-all duration-300"
      style={{
        background: scrolled
          ? "rgba(5,4,3,.94)"
          : "linear-gradient(to bottom, rgba(5,4,3,.85) 0%, transparent 100%)",
        backdropFilter: scrolled ? "blur(14px)" : "none",
      }}
    >
      <a
        href="/"
        onClick={onLogoClick}
        data-testid="nav-logo"
        className="flex items-center no-underline group st-interactive cursor-pointer"
        aria-label="Soul Trip — go to home"
        style={{
          opacity: logoReady ? 1 : 0,
          transition: "opacity .5s ease",
          background: "transparent",
        }}
      >
        {/* Sole brand mark — SoulTrip painted wordmark on transparent PNG.
            Responsive height: 52px on mobile → 76px on desktop. */}
        <SoulTripWordmark
          className="block transition-transform duration-500 group-hover:scale-[1.04] h-[52px] sm:h-[68px] md:h-[76px] w-auto"
          style={{ filter: "drop-shadow(0 2px 14px rgba(232,197,106,.22))" }}
        />
      </a>

      <div className="hidden md:flex items-center gap-7">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            data-testid={`nav-link-${l.label.toLowerCase()}`}
            className={({ isActive }) =>
              `st-cinzel text-[0.55rem] tracking-[0.32em] uppercase no-underline transition-colors ${
                isActive ? "text-[var(--gold)]" : "text-[var(--fog)] hover:text-[var(--gold)]"
              }`
            }
            end={l.to === "/"}
          >
            {l.label}
          </NavLink>
        ))}
        <a
          href={ETV_WIN_SERIES_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="nav-watch-btn"
          className="st-btn inline-flex items-center gap-2"
          style={{
            padding: "8px 16px",
            background: "#e21e26",
            borderColor: "#e21e26",
            color: "#ffffff",
          }}
        >
          <Play size={9} fill="currentColor"/> Watch on ETV Win
        </a>
      </div>

      <button
        data-testid="nav-mobile-toggle"
        aria-label="Toggle menu"
        className="md:hidden flex flex-col gap-1.5 p-2 st-interactive"
        onClick={() => setOpen((v) => !v)}
        style={{ background: "transparent", border: "none" }}
      >
        <span className="block h-px w-6 bg-[var(--gold)]" style={{ transform: open ? "translateY(4px) rotate(45deg)" : "none", transition: "transform .25s" }} />
        <span className="block h-px w-6 bg-[var(--gold)]" style={{ opacity: open ? 0 : 1, transition: "opacity .2s" }} />
        <span className="block h-px w-6 bg-[var(--gold)]" style={{ transform: open ? "translateY(-4px) rotate(-45deg)" : "none", transition: "transform .25s" }} />
      </button>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="md:hidden absolute top-full left-0 right-0 flex flex-col gap-1 px-6 py-6"
          style={{ background: "rgba(5,4,3,.98)", backdropFilter: "blur(14px)", borderTop: "1px solid rgba(201,160,74,.12)" }}
        >
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `st-cinzel text-[0.65rem] tracking-[0.32em] uppercase no-underline py-3 ${
                  isActive ? "text-[var(--gold)]" : "text-[var(--fog)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={ETV_WIN_SERIES_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-mobile-watch-btn"
            className="mt-3 st-btn inline-flex items-center justify-center gap-2"
            style={{ background: "#e21e26", borderColor: "#e21e26", color: "#ffffff" }}
          >
            <Play size={9} fill="currentColor"/> Watch on ETV Win
          </a>
        </div>
      )}
    </nav>
  );
};
